import { Stack, styled, Typography } from '@mui/material';

import FileArrowUpIcon from 'assets/icons/FileArrowUp.svg';
import SparkleIcon from 'assets/icons/Sparkle.svg';

/**
 * Fixed 48px frame for the glyph.
 *
 * Lives here rather than in `DragAndDrop` because the dropzone no longer prescribes what sits
 * inside it — it centres and gap-spaces whatever the caller hands over, and the framing is
 * part of what this surface chose to hand over.
 */
const IconTile = styled(Stack)(({ theme }) => ({
    width: 48,
    height: 48,
    borderRadius: 8,
    // One step darker than the dropzone's own cement fill, or the tile disappears into it.
    backgroundColor: theme.palette.cement[200],
}));

interface StatementDropzoneContentProps {
    /** The constraints line. Built by the caller from the limits it passes the uploader. */
    readonly hint: string;
}

/**
 * What the statement dropzone says when idle.
 *
 * Owning this here is the point of `DragAndDrop`'s `idleContent` slot: the shape of the message
 * — a framed glyph, a heading, a line about matching, then the constraints — is this surface's
 * decision, and adding to it needs no change to the shared uploader. The cost is that the type
 * styles are restated here rather than centralised, which is the trade the slot makes.
 *
 * The spacing is owned here too, in a single wrapping `Stack`, rather than left to the container's
 * uniform `gap`: the constraints line sits further from the message (24px) than the glyph does
 * (16px), and one root child is what lets this surface say so.
 */
export default function StatementDropzoneContent({ hint }: Readonly<StatementDropzoneContentProps>) {
    return (
        <Stack direction="column" alignItems="center" spacing={3}>
            <Stack direction="column" alignItems="center" spacing={2}>
                <IconTile alignItems="center" justifyContent="center">
                    <img src={FileArrowUpIcon} alt="" width={24} height={24} />
                </IconTile>

                <Stack direction="column" justifyContent="center" alignItems="center" spacing={0.5}>
                    <Typography variant="bodySemiBold" textAlign="center">
                        Drag them all in or click to upload
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <img src={SparkleIcon} alt="" width={16} height={16} />
                        <Typography variant="body2" color="text.secondary">
                            We&apos;ll match each file to the right statement for you
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary" textAlign="center">
                {hint}
            </Typography>
        </Stack>
    );
}
